import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import GuidedUpsell from '../../components/GuidedUpsell';
import SubmitHousingModal from '../../components/SubmitHousingModal';
import HousingContactModal from '../../components/HousingContactModal';
import { ROLES } from '../../constants/tiers';
import {
  Home as HomeIcon, Users, Lock, ExternalLink, Plus,
  Bed, Bath, Calendar, MapPin, Loader2, MessageSquare,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LISTING_TYPE_LABEL = {
  rent: 'Rent',
  roommate: 'Roommate',
  sublet: 'Sublet',
};

const fmtDate = (s) => {
  if (!s) return '';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/Chicago'});
  } catch {
    return s;
  }
};

const StatusBadge = ({ status }) => {
  if (status === 'pending') {
    return (
      <span
        className="inline-block bg-[#E8A830]/20 text-[#8A6310] border border-[#E8A830]/50 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1"
        data-testid="housing-listing-status-pending"
      >
        Pending review
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span
        className="inline-block bg-[#B91C1C]/10 text-[#7F1414] border border-[#B91C1C]/40 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1"
        data-testid="housing-listing-status-rejected"
      >
        Not approved
      </span>
    );
  }
  return null;
};

const ListingCard = ({ listing, onContact, isMine = false, currentUserId }) => {
  const hero = listing.photos?.[0];
  const canContact = !isMine && currentUserId && listing.submitted_by_user_id && listing.submitted_by_user_id !== currentUserId;
  return (
    <div
      className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden hover:border-[#2CA8A0]/50 transition flex flex-col"
      data-testid={`housing-listing-${listing.id}`}
    >
      <div className="aspect-[4/3] bg-[#F4ECD8] relative overflow-hidden">
        {hero ? (
          <img
            src={hero}
            alt={listing.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <HomeIcon className="w-10 h-10 text-[#1A5C6E]/30" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <span className="bg-[#0A1F2E] text-white rounded-full font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1">
            {LISTING_TYPE_LABEL[listing.listing_type] || listing.listing_type}
          </span>
          {isMine && <StatusBadge status={listing.status} />}
        </div>
        {listing.photos?.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-[#0A1F2E]/75 text-white text-[10px] font-mono rounded-full px-2 py-0.5">
            +{listing.photos.length - 1} photos
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-1 text-[#1A5C6E] mb-1">
          <MapPin className="w-3.5 h-3.5" />
          <span className="font-mono uppercase tracking-[0.12em] text-[10px]">{listing.city}</span>
        </div>
        <h3 className="font-display italic text-xl text-[#0A1F2E] mb-2 leading-tight">
          {listing.title}
        </h3>
        <p className="text-sm text-[#0A1F2E]/70 mb-4 line-clamp-3 leading-snug">
          {listing.description}
        </p>

        <div className="mt-auto space-y-3">
          <div className="flex items-baseline justify-between">
            <p className="font-display italic text-2xl text-[#0A1F2E]">
              ${Number(listing.price_monthly_usd || 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}
              <span className="text-xs font-mono uppercase tracking-[0.12em] text-[#0A1F2E]/55 ml-1">/ mo</span>
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#0A1F2E]/70 font-mono">
            <span className="inline-flex items-center gap-1"><Bed className="w-3.5 h-3.5" /> {listing.bedrooms} bd</span>
            <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" /> {listing.bathrooms} ba</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(listing.available_date)}</span>
          </div>
          {canContact && (
            <button
              type="button"
              onClick={() => onContact(listing)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white rounded-full px-4 py-2.5 font-mono uppercase text-[11px] tracking-[0.15em] transition"
              data-testid={`housing-listing-contact-${listing.id}`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Message submitter
            </button>
          )}
          {isMine && (
            <p className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#0A1F2E]/55 text-center pt-1">
              Your listing
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const LockedState = () => (
  <div className="max-w-2xl mx-auto bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl p-8 md:p-10 text-center" data-testid="housing-locked">
    <Lock className="w-8 h-8 text-[#1A5C6E] mx-auto mb-4" />
    <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-3">Housing is a Community perk.</h2>
    <p className="font-body text-base text-[#0A1F2E]/75 mb-6">
      Browse Mundalo-curated rentals and find Mundalo members looking for a
      roommate in your destination city.
    </p>
    <a
      href="/portal/upgrade"
      className="inline-flex items-center gap-2 bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.16em] font-semibold py-3 px-6 rounded-full transition"
      data-testid="housing-upgrade-cta"
    >
      Upgrade to Community - $9.99/mo
    </a>
  </div>
);

export default function PortalHousing() {
  const { user } = useAuth();
  const role = user?.role || ROLES.FREE_TRIAL;
  const hasAccess = [ROLES.COMMUNITY, ROLES.CONNECTED, ROLES.ADMIN].includes(role);

  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [contactListing, setContactListing] = useState(null);

  const reload = async () => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [pubR, mineR] = await Promise.all([
        fetch(`${API}/api/housing/listings`, { credentials: 'include' }),
        fetch(`${API}/api/housing/listings/mine`, { credentials: 'include' }),
      ]);
      if (pubR.ok) {
        const d = await pubR.json();
        setListings(d.listings || []);
      }
      if (mineR.ok) {
        const d = await mineR.json();
        setMyListings(d.listings || []);
      }
    } catch {
      // Empty state already handles failure visually.
    }
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  // Show submitter their own pending listings inline at the top, then any
  // approved listings (deduped by id to avoid showing twice).
  const pendingMine = useMemo(
    () => myListings.filter((l) => l.status === 'pending' || l.status === 'rejected'),
    [myListings],
  );
  const visibleListings = useMemo(() => {
    const seen = new Set(pendingMine.map((l) => l.id));
    return listings.filter((l) => !seen.has(l.id));
  }, [listings, pendingMine]);

  const cities = useMemo(() => {
    const s = new Set(visibleListings.map((l) => l.city).filter(Boolean));
    return Array.from(s).sort();
  }, [visibleListings]);

  const filtered = useMemo(() => {
    return visibleListings.filter(
      (l) => (!cityFilter || l.city === cityFilter) && (!typeFilter || l.listing_type === typeFilter),
    );
  }, [visibleListings, cityFilter, typeFilter]);

  return (
    <PortalLayout activeTab="housing">
      <SEO
        title="Housing - Mundalo"
        description="Member housing listings + roommate finder for Mundalo members in Mexico."
        path="/portal/housing"
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="portal-housing-page">
        <GuidedUpsell variant="card" storageKey="housing" />

        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            The Life · Housing
          </p>
          <h1
            className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3"
            data-testid="housing-headline"
          >
            Find your place.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Live listings posted by Mundalo members - rentals, roommates,
            sublets. Reach out directly through the platform.
          </p>
        </header>

        {!hasAccess ? (
          <LockedState />
        ) : (
          <div className="space-y-12">
            {/* === COMMUNITY LISTINGS === */}
            <section data-testid="housing-listings-section">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-display italic text-3xl text-[#0A1F2E] flex items-center gap-3">
                  <HomeIcon className="w-6 h-6 text-[#E8A830]" />
                  Community Listings
                </h2>
                <button
                  type="button"
                  onClick={() => setSubmitOpen(true)}
                  className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-2.5 px-4 rounded-full transition"
                  data-testid="housing-open-submit"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Submit a listing
                </button>
              </div>

              {/* Filters - only render if there are listings to filter */}
              {(filtered.length > 0 || cities.length > 0) && (
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
                    data-testid="housing-filter-city"
                  >
                    <option value="">All cities</option>
                    {cities.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
                    data-testid="housing-filter-type"
                  >
                    <option value="">All types</option>
                    <option value="rent">Rent</option>
                    <option value="roommate">Roommate</option>
                    <option value="sublet">Sublet</option>
                  </select>
                </div>
              )}

              {/* Loading */}
              {loading && (
                <div className="text-center py-12">
                  <Loader2 className="w-6 h-6 text-[#2CA8A0] animate-spin mx-auto" />
                </div>
              )}

              {/* My pending / rejected listings - visible to me only */}
              {!loading && pendingMine.length > 0 && (
                <div className="mb-8" data-testid="housing-my-pending">
                  <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-3">
                    Your listings · awaiting review
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {pendingMine.map((l) => (
                      <ListingCard
                        key={l.id}
                        listing={l}
                        isMine
                        currentUserId={user?.id}
                        onContact={() => {}}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Active listings grid */}
              {!loading && filtered.length === 0 && pendingMine.length === 0 && (
                <div className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center" data-testid="housing-empty-state">
                  <HomeIcon className="w-12 h-12 text-[#1A5C6E]/30 mx-auto mb-4" />
                  <p className="font-display italic text-2xl text-[#0A1F2E] mb-2">No listings yet.</p>
                  <p className="text-sm text-[#0A1F2E]/65 max-w-md mx-auto">
                    Be the first to share a place. Submitted listings are reviewed
                    by the Mundalo team and go live within a day.
                  </p>
                </div>
              )}

              {!loading && filtered.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filtered.map((l) => (
                    <ListingCard
                      key={l.id}
                      listing={l}
                      currentUserId={user?.id}
                      onContact={(listing) => setContactListing(listing)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* === ROOMMATES === */}
            <section data-testid="housing-roommates-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display italic text-3xl text-[#0A1F2E] flex items-center gap-3">
                  <Users className="w-6 h-6 text-[#E8A830]" />
                  Roommate Finder
                </h2>
                <a
                  href="/portal/community?board=roommates"
                  className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-2.5 px-4 rounded-full transition"
                  data-testid="housing-open-roommates"
                >
                  Open roommate board <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <p className="font-body text-base text-[#0A1F2E]/70 leading-relaxed">
                Other Mundalo members actively looking to share a place in
                your destination city. Filter by city, budget, move-in date,
                and lifestyle.
              </p>
            </section>
          </div>
        )}
      </div>

      <SubmitHousingModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmitted={reload}
      />
      <HousingContactModal
        open={!!contactListing}
        onClose={() => setContactListing(null)}
        listing={contactListing}
      />
    </PortalLayout>
  );
}
